
import { AttendancePattern } from '../types';
import { notificationManager } from '../NotificationManagerInstance';

export class AttendanceNotificationService {
  // Store previously sent notification patterns to prevent duplicates
  private sentNotifications: Map<string, { timestamp: number, pattern: AttendancePattern }> = new Map();
  
  // Default threshold that can be overridden
  private absenceThreshold: number = 3;
  private latenessThreshold: number = 4;
  
  // Set custom thresholds
  public setThresholds(absenceThreshold: number, latenessThreshold: number): void {
    if (absenceThreshold > 0) this.absenceThreshold = absenceThreshold;
    if (latenessThreshold > 0) this.latenessThreshold = latenessThreshold;
  }
  
  // Get current thresholds
  public getThresholds(): { absence: number, lateness: number } {
    return {
      absence: this.absenceThreshold,
      lateness: this.latenessThreshold
    };
  }

  public processAttendancePatterns(attendanceRecords: any[]): AttendancePattern[] {
    const patterns: AttendancePattern[] = [];
    const teacherAbsenceMap = new Map<string, { count: number, dates: Date[] }>();
    const teacherLatenessMap = new Map<string, { count: number, dates: Date[] }>();
    
    attendanceRecords.forEach(record => {
      const teacherId = record.teacherId;
      
      if (record.status === 'absent') {
        if (!teacherAbsenceMap.has(teacherId)) {
          teacherAbsenceMap.set(teacherId, { count: 0, dates: [] });
        }
        
        const teacherData = teacherAbsenceMap.get(teacherId)!;
        teacherData.count += 1;
        teacherData.dates.push(new Date(record.date));
      }
      
      if (record.status === 'late') {
        if (!teacherLatenessMap.has(teacherId)) {
          teacherLatenessMap.set(teacherId, { count: 0, dates: [] });
        }
        
        const teacherData = teacherLatenessMap.get(teacherId)!;
        teacherData.count += 1;
        teacherData.dates.push(new Date(record.date));
      }
    });
    
    // Process absence patterns
    teacherAbsenceMap.forEach((data, teacherId) => {
      if (data.count >= this.absenceThreshold) {
        // Convert Date objects to ISO strings
        const stringDates = data.dates.map(d => d.toISOString());
        const lastOccurrence = new Date(Math.max(...data.dates.map(d => d.getTime()))).toISOString();
        
        const pattern: AttendancePattern = {
          teacherId,
          type: 'absence',
          patternType: 'frequent-absence',
          count: data.count,
          occurrences: data.count,
          dates: stringDates,
          lastOccurrence,
          threshold: this.absenceThreshold,
          metadata: {
            firstOccurrence: new Date(Math.min(...data.dates.map(d => d.getTime()))).toISOString(),
            lastOccurrence
          }
        };
        
        // Check for duplicates before adding
        if (!this.isDuplicateNotification(teacherId, 'frequent-absence', stringDates)) {
          patterns.push(pattern);
        }
      }
    });
    
    // Process lateness patterns
    teacherLatenessMap.forEach((data, teacherId) => {
      if (data.count >= this.latenessThreshold) {
        // Convert Date objects to ISO strings
        const stringDates = data.dates.map(d => d.toISOString());
        const lastOccurrence = new Date(Math.max(...data.dates.map(d => d.getTime()))).toISOString();
        
        const pattern: AttendancePattern = {
          teacherId,
          type: 'lateness',
          patternType: 'late-arrival',
          count: data.count,
          occurrences: data.count,
          dates: stringDates,
          lastOccurrence,
          threshold: this.latenessThreshold,
          metadata: {
            firstOccurrence: new Date(Math.min(...data.dates.map(d => d.getTime()))).toISOString(),
            lastOccurrence
          }
        };
        
        // Check for duplicates before adding
        if (!this.isDuplicateNotification(teacherId, 'late-arrival', stringDates)) {
          patterns.push(pattern);
        }
      }
    });
    
    return patterns;
  }
  
  // Helper method to check for duplicate notifications
  private isDuplicateNotification(
    teacherId: string, 
    patternType: 'frequent-absence' | 'late-arrival', 
    dates: string[]
  ): boolean {
    // Create a unique key for this notification pattern
    const notificationKey = `${teacherId}-${patternType}-${this.getDateRangeHash(dates)}`;
    
    // Check if we've already sent this notification within the cooldown period (7 days)
    const existingNotification = this.sentNotifications.get(notificationKey);
    const now = Date.now();
    const cooldownPeriod = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    
    if (existingNotification && (now - existingNotification.timestamp) < cooldownPeriod) {
      return true; // This is a duplicate, don't send it
    }
    
    // Record this notification as sent
    this.sentNotifications.set(notificationKey, {
      timestamp: now,
      pattern: {
        teacherId,
        type: patternType === 'frequent-absence' ? 'absence' : 'lateness',
        patternType,
        count: dates.length,
        occurrences: dates.length,
        dates,
        lastOccurrence: dates[dates.length - 1],
        threshold: patternType === 'frequent-absence' ? this.absenceThreshold : this.latenessThreshold
      }
    });
    
    // Clean up old notifications (more than 30 days old)
    this.cleanupOldNotifications();
    
    return false;
  }
  
  // Helper to create a hash of date ranges for deduplication
  private getDateRangeHash(dates: string[]): string {
    if (dates.length === 0) return '';
    
    // Sort dates
    const sortedDates = [...dates].sort();
    
    // Get first and last date
    const firstDate = sortedDates[0];
    const lastDate = sortedDates[sortedDates.length - 1];
    
    return `${firstDate}-${lastDate}-${dates.length}`;
  }
  
  // Clean up old notifications to prevent memory leaks
  private cleanupOldNotifications(): void {
    const now = Date.now();
    const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    
    this.sentNotifications.forEach((value, key) => {
      if (now - value.timestamp > maxAge) {
        this.sentNotifications.delete(key);
      }
    });
  }

  public async sendAttendancePatternNotification(
    pattern: AttendancePattern, 
    teacherName?: string
  ): Promise<boolean> {
    try {
      const title = 'Attendance Pattern Detected';
      let message = '';
      let priority: 'low' | 'medium' | 'high' = 'medium';
      
      switch (pattern.patternType) {
        case 'frequent-absence':
          message = `${teacherName || 'A teacher'} has been absent ${pattern.occurrences} times recently.`;
          priority = pattern.occurrences >= this.absenceThreshold * 2 ? 'high' : 'medium';
          break;
        case 'late-arrival':
          message = `${teacherName || 'A teacher'} has arrived late ${pattern.occurrences} times recently.`;
          priority = pattern.occurrences >= this.latenessThreshold * 2 ? 'high' : 'medium';
          break;
        default:
          message = `An attendance pattern has been detected for ${teacherName || 'a teacher'}.`;
      }
      
      // Add date range to the message for context
      if (pattern.metadata?.firstOccurrence && pattern.metadata?.lastOccurrence) {
        message += ` (${pattern.metadata.firstOccurrence.substring(0, 10)} - ${pattern.metadata.lastOccurrence.substring(0, 10)})`;
      }
      
      // Create notification with enhanced metadata
      notificationManager.createTeacherNotification(
        title,
        message,
        pattern.teacherId,
        teacherName,
        'attendance',
        priority
      );
      
      return true;
    } catch (error) {
      console.error('Error sending attendance pattern notification:', error);
      return false;
    }
  }
}
